import { useMemo, useState } from "react";
import { JobStatus } from "@prisma/client";
import { Label } from "@radix-ui/react-label";
import { ChevronDown, Loader2 } from "lucide-react";
import { DevicePreset } from "@/lib/viewports";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ViewportSelector } from "@/components/ui/viewport-selector";


export interface ScreenshotState {
  url: string;
  device: string;
  snapshotId?: string;
  status?: JobStatus;
  screenshotUrl?: string;
  error?: string;
}

export function CapturePanel({
  title,
  state,
  onStateChange,
  onCapture,
  devicePresets,
  isPending,
  canSelectDevice = true,
  deviceInfoHint,
  isChallengeReady = true,
  challengeHelperText,
}: {
  title: string;
  state: ScreenshotState;
  onStateChange: (next: ScreenshotState) => void;
  onCapture: () => void;
  devicePresets: DevicePreset[];
  isPending: boolean;
  canSelectDevice?: boolean;
  deviceInfoHint?: string;
  isChallengeReady?: boolean;
  challengeHelperText?: string;
}) {
  const slug = useMemo(() => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  }, [title]);
  const selectedDevice = useMemo(() => {
    return devicePresets.find((preset) => preset.id === state.device);
  }, [devicePresets, state.device]);
  const [isExpanded, setIsExpanded] = useState(false);

  // useEffect(() => {
  //   if (typeof window === "undefined") {
  //     return;
  //   }
  //   const isSmallScreen = window.matchMedia("(max-width: 767px)").matches;
  //   if (isSmallScreen) {
  //     setIsExpanded(false);
  //   }
  // }, []);

  return (
    <div className="flex h-full flex-col gap-4 rounded-xl border bg-card p-6 shadow-sm">
      <div className="space-y-2">
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="text-sm text-muted-foreground">
          Provide a public URL and choose a viewport to capture a fresh
          screenshot.
        </p>
      </div>

      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor={`${slug}-url`}>URL</Label>
          <Input
            id={`${slug}-url`}
            value={state.url}
            placeholder="https://example.com"
            onChange={(event) =>
              onStateChange({ ...state, url: event.target.value })
            }
            disabled={isPending}
            type="url"
          />
        </div>

        <div className="space-y-1">
          <Label>{canSelectDevice ? "Select viewport" : "Viewport"}</Label>
          {canSelectDevice ? (
            <ViewportSelector
              options={devicePresets}
              value={state.device}
              onValueChange={(value) =>
                onStateChange({ ...state, device: value })
              }
              disabled={isPending || devicePresets.length === 0}
              placeholder="Choose a viewport"
            />
          ) : (
            <div className="rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground">
              {selectedDevice
                ? `${selectedDevice.label} (${selectedDevice.width}×${selectedDevice.height} @ DPR${selectedDevice.deviceScaleFactor ?? 1})`
                : "Select a viewport in the baseline panel"}
            </div>
          )}
          {deviceInfoHint ? (
            <p className="text-xs text-muted-foreground">{deviceInfoHint}</p>
          ) : null}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <Button
          onClick={onCapture}
          disabled={
            isPending ||
            !state.url.trim() ||
            devicePresets.length === 0 ||
            !isChallengeReady
          }
          className="w-full"
        >
          {isPending ? (
            <span className="flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              Capturing Screenshot...
            </span>
          ) : (
            "Capture Screenshot"
          )}
        </Button>
        {!isChallengeReady ? (
          <p className="text-sm text-muted-foreground">
            {challengeHelperText ??
              "Complete the Turnstile challenge to unlock captures."}
          </p>
        ) : null}
        {state.error ? (
          <p className="text-sm text-destructive">{state.error}</p>
        ) : null}
      </div>
      <div
        className={cn(
          "relative flex flex-1 flex-col overflow-hidden rounded-lg border bg-muted",
          "min-h-[220px]",
          isExpanded ? "max-h-full" : "max-h-[250px]",
        )}
      >
        <div
          className={cn(
            "flex flex-1 overflow-scroll",
            state.screenshotUrl
              ? "items-start justify-center"
              : "items-center justify-center",
          )}
        >
          {state.screenshotUrl ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={state.screenshotUrl}
                alt={`Screenshot for ${title}`}
                className={cn(
                  "w-full object-top",
                  isExpanded ? "max-h-full object-contain" : "object-contain",
                )}
              />
            </>
          ) : (
            <div className="flex flex-col items-center px-6 text-center text-sm text-muted-foreground">
              {isPending
                ? "Waiting for screenshot..."
                : "Screenshot preview will appear here once captured."}
              {selectedDevice ? (
                <div className="mt-2 text-xs">
                  {`${selectedDevice.label} - ${selectedDevice.width}x${selectedDevice.height}`}
                </div>
              ) : null}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded((previous) => !previous)}
          className="flex w-full items-center justify-center border-t bg-muted/80 py-2 text-muted-foreground transition hover:bg-muted"
        >
          <ChevronDown
            className={cn(
              "size-5 transition-transform",
              isExpanded ? "rotate-180" : "rotate-0",
            )}
          />
        </button>
      </div>
    </div>
  );
}